<template>
  <!-- Same row layout as the Payments tab (charges.scss), so it stacks the same way on phones -->
  <tbody class="charge-row" :class="{ 'is-refunding': markingPaid || editingNotes }">
    <tr class="charge-row__main">
      <td class="charge-row__amount">
        <div class="charge-row__amount-line">{{ formatAsCurrency(row.amount || 0) }}</div>
      </td>
      <td class="charge-row__status">
        <div class="charge-row__status-line">
          <span class="charge-badge" :class="`charge-badge--${status.tone}`"><i :class="status.icon" aria-hidden="true"></i> {{ status.label }}</span>
        </div>
        <div v-if="detail" class="charge-row__detail" :class="`charge-row__detail--${status.tone}`">{{ detail }}</div>
      </td>
      <td class="charge-row__for">
        <div class="charge-row__title">{{ row.title }}</div>
        <div class="charge-row__meta">{{ meta }}</div>
        <div v-if="row.wrestlers.length > 0" class="charge-row__for-wrestlers">
          <template v-for="(wrestler, index) in row.wrestlers"><template v-if="index > 0">, </template><a :key="wrestler.id" :href="`/wrestlers/${wrestler.id}`" target="_blank">{{ wrestler.full_name }}</a></template>
        </div>
      </td>
      <td class="charge-row__billed-to">
        <a v-if="billedToUrl" :href="billedToUrl" target="_blank">{{ billedToName }}</a>
        <template v-else>{{ billedToName }}</template>
      </td>
      <td class="charge-row__wrestlers">
        <template v-if="row.wrestlers.length > 0">
          <div v-for="wrestler in row.wrestlers" :key="wrestler.id"><a :href="`/wrestlers/${wrestler.id}`" target="_blank">{{ wrestler.full_name }}</a></div>
        </template>
        <span v-else class="charge-row__none">—</span>
      </td>
      <td class="charge-row__date">
        <span>{{ when }}</span>
        <span class="charge-row__date-payer"> · Billed to <a v-if="billedToUrl" :href="billedToUrl" target="_blank">{{ billedToName }}</a><template v-else>{{ billedToName }}</template></span>
      </td>
      <td class="charge-row__actions">
        <div class="charge-row__buttons">
          <button v-if="markPaidInvoice && !markingPaid" type="button" class="btn btn-default btn-compact" @click="markingPaid = true">{{ markPaidLabel }}</button>
          <a v-if="row.subscription_billing_partner_id" :href="`/subscriptions/${row.subscription_billing_partner_id}`" target="_blank" class="btn btn-default btn-compact">Manage subscription</a>
          <!-- Once there's a note, it's edited from the note line -->
          <button v-if="!editingNotes && !row.notes" type="button" class="btn btn-default btn-compact" @click="editingNotes = true">
            <i class="fa-regular fa-note-sticky" aria-hidden="true"></i> Add note
          </button>
        </div>
      </td>
    </tr>
    <!-- The note on its own line, starting under For -->
    <tr v-if="row.notes && !editingNotes" class="charge-row__note-row">
      <td colspan="2" class="charge-row__note-spacer"></td>
      <td colspan="5"><row-note :text="row.notes" :editable="true" @edit="editingNotes = true"></row-note></td>
    </tr>
    <tr v-if="markingPaid" class="charge-row__refund">
      <td colspan="7">
        <mark-paid-form :manual-invoice="markPaidInvoice"></mark-paid-form>
      </td>
    </tr>
    <tr v-if="editingNotes" class="charge-row__refund">
      <td colspan="7">
        <div class="form-group">
          <textarea v-model="draftNotes" class="form-control" rows="2" style="resize: none" placeholder="Who you talked to, what they said, when they'll pay.."></textarea>
          <div class="flex gap-xs m-t-xs">
            <ladda-button @lbClicked="saveNotes" el-class="btn-primary" :loading="saving">
              Save
            </ladda-button>
            <button v-show="!saving" type="button" class="btn btn-default" @click="editingNotes = false">
              Cancel
            </button>
          </div>
          <p v-if="error" class="text-danger m-t-xs text-12">
            Error saving note
          </p>
        </div>
      </td>
    </tr>
  </tbody>
</template>
<script>
import LaddaButton from '../../shared/ladda_button.vue';
import MarkPaidForm from './mark_paid_form.vue';
import RowNote from './row_note.vue';
import { formattingMixin } from './mixins/formatting_mix';

const CASH_METHODS = ['cash', 'check'];
const RESOLUTIONS = {
  paid: { label: 'Paid', tone: 'success', icon: 'fa-solid fa-check' },
  canceled_for_nonpayment: { label: 'Canceled', tone: 'neutral', icon: 'fa-solid fa-ban' },
  canceled: { label: 'Canceled', tone: 'neutral', icon: 'fa-solid fa-ban' },
  cleared: { label: 'No longer overdue', tone: 'neutral', icon: 'fa-solid fa-check' },
  // Only the reminder closed; the payment is still overdue and can be marked paid from the family's Payments tab
  auto_closed: { label: 'Closed automatically', tone: 'neutral', icon: 'fa-regular fa-clock' },
};

// One PaymentFollowUp on the payments dashboard's Needs follow-up tab: an
// unpaid payment plan or subscription, or (Recently resolved) one that's been
// paid or canceled. Admins mark cash / check paid here and keep notes.
export default {
  name: 'payment-follow-up-row',
  components: { LaddaButton, MarkPaidForm, RowNote },
  mixins: [formattingMixin],
  props: {
    row: { type: Object, required: true },
  },
  data() {
    return {
      markingPaid: false,
      // Resolving happens in the background after Mark paid; show it right away
      markedPaid: false,
      editingNotes: false,
      draftNotes: this.row.notes || '',
      saving: false,
      error: false,
    };
  },
  computed: {
    plan() {
      return this.row.followable_type === 'PaymentPlan';
    },
    resolved() {
      return !!this.row.resolved_at;
    },
    status() {
      if (this.markedPaid) {
        return RESOLUTIONS.paid;
      }
      if (this.resolved) {
        return RESOLUTIONS[this.row.resolution] || RESOLUTIONS.cleared;
      }
      if (this.row.reason === 'card_failing') {
        return { label: 'Card failing', tone: 'danger', icon: 'fa-solid fa-xmark' };
      }
      if (this.plan && CASH_METHODS.includes(this.row.payment_method)) {
        return { label: `${_.capitalize(this.row.payment_method)} due`, tone: 'warning', icon: 'fa-solid fa-money-bill' };
      }
      return { label: 'Overdue', tone: 'warning', icon: 'fa-regular fa-clock' };
    },
    attempts() {
      if (this.row.failures_count === 0) {
        return null;
      }
      const of = this.row.max_failures ? ` of ${this.row.max_failures}` : '';
      return `${this.row.failures_count}${of} ${this.row.failures_count === 1 && !of ? 'attempt' : 'attempts'} failed`;
    },
    detail() {
      if (this.markedPaid) {
        return 'Marked paid';
      }
      if (this.resolved) {
        if (this.row.resolution === 'auto_closed') {
          return 'Open 30 days with no payment · still overdue';
        }
        return this.row.resolution === 'canceled_for_nonpayment' ? `For non-payment${this.attempts ? ` · ${this.attempts}` : ''}` : null;
      }
      if (this.row.reason === 'card_failing') {
        return _.compact([this.attempts, this.row.last_decline_reason]).join(' · ');
      }
      if (!this.plan) {
        return 'Not retried automatically';
      }
      return null;
    },
    meta() {
      if (!this.plan) {
        return 'Recurring Subscription';
      }
      const count = this.row.overdue_installments_count || 0;
      return count > 1 ? `${count} installments overdue` : 'Installment overdue';
    },
    when() {
      if (this.resolved) {
        return `Resolved ${this.shortDate(this.row.resolved_at)}`;
      }
      if (this.row.reason === 'card_failing') {
        if (this.plan) {
          return 'Retries nightly';
        }
        if (!this.row.next_retry_at) {
          return 'Retried by Stripe';
        }
        return moment(this.row.next_retry_at).isAfter(moment()) ? `Next retry ${this.shortDate(this.row.next_retry_at)}` : 'Retrying soon';
      }
      return this.row.oldest_due_on ? `Due ${this.shortDate(this.row.oldest_due_on)}` : `Since ${this.shortDate(this.row.opened_at)}`;
    },
    // Cash / check are collected one installment at a time, oldest first
    markPaidInvoice() {
      if (!this.plan || this.resolved || this.markedPaid || !this.row.needs_coach) {
        return null;
      }
      return _.first(this.row.installments) || null;
    },
    markPaidLabel() {
      return this.row.installments.length > 1 ? `Mark ${this.shortDate(this.row.installments[0].due_on)} paid…` : 'Mark paid…';
    },
    billedToName() {
      if (!this.row.billed_to) {
        return 'Unknown';
      }
      return this.row.billed_to.display_name || this.row.billed_to.email;
    },
    billedToUrl() {
      const paths = { ParentProfile: 'parents', CoachProfile: 'coaches' };
      return this.row.billed_to && paths[this.row.billed_to.type] ? `/${paths[this.row.billed_to.type]}/${this.row.billed_to.id}` : null;
    },
  },
  created() {
    this.$notificationManager.on('invoice-paid', this.invoicePaid);
    this.$notificationManager.on('hide-mark-paid-form', this.hideMarkPaid);
  },
  destroyed() {
    this.$notificationManager.off('invoice-paid', this.invoicePaid);
    this.$notificationManager.off('hide-mark-paid-form', this.hideMarkPaid);
  },
  methods: {
    invoicePaid(invoice) {
      if (!this.markPaidInvoice || invoice.id !== this.markPaidInvoice.id) {
        return;
      }
      this.markingPaid = false;
      // Later installments still overdue keep the row open
      if (this.row.installments.length > 1) {
        this.row.installments.shift();
        this.row.overdue_installments_count -= 1;
        this.row.amount -= invoice.amount_due;
      } else {
        this.markedPaid = true;
      }
    },
    hideMarkPaid(invoice) {
      if (this.markPaidInvoice && invoice.id === this.markPaidInvoice.id) {
        this.markingPaid = false;
      }
    },
    saveNotes: _.throttle(function () {
      this.saving = true;
      this.error = false;
      axios.put(this.$apiService.paymentFollowUpUrl(this.row.id), { payment_follow_up: { notes: this.draftNotes } })
        .then(() => {
          this.row.notes = this.draftNotes;
          this.editingNotes = false;
          this.saving = false;
          this.$notificationManager.emit('show-toast', 'Note saved', true);
        })
        .catch(() => {
          this.error = true;
          this.saving = false;
        });
    }, 500),
    shortDate(value) {
      const date = moment(value);
      return date.year() === moment().year() ? date.format('MMM D') : date.format('MMM D, YYYY');
    },
  },
};
</script>
